import React from "react";
import { Link } from "react-router-dom";
import coachDeadlift from "../assets/images/about-coach-deadlift.jpeg";
import coachPortrait from "../assets/images/about-coach-portrait.jpeg";
import { useLanguage } from "../i18n/LanguageContext";

// ── Step-by-step how it works ─────────────────────────────────────────────────
const steps = [
  {
    number: "01",
    icon: "fa-user-plus",
    title: "Create Your Account",
    desc: "Register in seconds. Your free account gives you access to browse all available libraries, categories and preview content before committing.",
  },
  {
    number: "02",
    icon: "fa-box-open",
    title: "Choose a Package",
    desc: "Each library is unlocked by subscribing to the matching package. Pick the plan that fits your training goals — monthly or annual options available.",
  },
  {
    number: "03",
    icon: "fa-layer-group",
    title: "Explore Categories",
    desc: "Inside every library, content is organised into focused categories (e.g. Strength, Mobility, Sport-Specific). Navigate straight to what matters to you.",
  },
  {
    number: "04",
    icon: "fa-circle-play",
    title: "Watch & Train",
    desc: "Stream HD coaching videos on any device. Each video is a self-contained lesson — watch in order or jump to the topic you need, on your schedule.",
  },
];

// ── Platform feature highlights ───────────────────────────────────────────────
const features = [
  {
    icon: "fa-film",
    title: "HD Video Library",
    desc: "Every programme is delivered through high-quality, professionally filmed coaching videos hosted on a fast global CDN.",
  },
  {
    icon: "fa-lock-open",
    title: "Subscription Access",
    desc: "One subscription unlocks an entire library. No per-video fees, no hidden paywalls — full access for the duration of your plan.",
  },
  {
    icon: "fa-mobile-screen",
    title: "Any Device, Any Time",
    desc: "The platform is fully responsive. Train at home, in the gym, or on the move — your content follows you everywhere.",
  },
  {
    icon: "fa-cubes",
    title: "Structured Content",
    desc: "Content is grouped into libraries → categories → videos so you always know where you are in your learning journey.",
  },
  {
    icon: "fa-chart-line",
    title: "Evidence-Based Methods",
    desc: "All programmes are built on proven exercise science — no gimmicks, just practical methods that deliver long-term results.",
  },
  {
    icon: "fa-headset",
    title: "Direct Support",
    desc: "Have a question or issue? Reach the team directly via the contact form and get a personal response from the coaching staff.",
  },
];

// ── Check-list items ──────────────────────────────────────────────────────────
const comparisonRows = [
  ["Train for appearance only", "Build physique + performance"],
  ["Random exercise selection", "Structured training"],
  ["Search endlessly online", "Everything organized"],
  ["One training method", "Multiple performance systems"],
  ["Generic fitness advice", "Purpose-driven training"],
];

// ─────────────────────────────────────────────────────────────────────────────
const AboutServices = () => {
  const { t } = useLanguage();
  return (
    <>
      {/* ── Hero row ── */}
      <div className="row align-items-center mb-5">
        <div className="col-lg-6">
          <div className="hpa-platform-gallery hpa-home-platform-gallery wow fadeInUp">
            <figure className="hpa-platform-card hpa-platform-card-action">
              <img
                src={coachDeadlift}
                alt="Hybrid Performance Academy coach deadlifting"
                className="hpa-platform-photo"
              />
              <figcaption>
                <i className="flaticon-athletics" aria-hidden="true"></i>
                Performance in action
              </figcaption>
            </figure>

            <figure className="hpa-platform-card hpa-platform-card-portrait">
              <img
                src={coachPortrait}
                alt="Hybrid Performance Academy coach"
                className="hpa-platform-photo"
              />
              <figcaption>
                <i className="flaticon-check-mark" aria-hidden="true"></i>
                Expert-led coaching
              </figcaption>
            </figure>
          </div>
        </div>

        <div className="col-lg-6 about-content m-lg-t40 wow fadeInUp">
          <div className="section-head style-1">
            <h5 className="sub-title">{t("HYBRID PERFORMANCE ACADEMY")}</h5>
            <h2 className="title mb-0 hpa-positioning-title">
              <span>{t("BUILD THE BODY.")}</span>
              <span>{t("DEVELOP THE ATHLETE.")}</span>
            </h2>
            <p className="description hpa-positioning-subheadline">
              {t("Train for muscle, strength and performance—without sacrificing one for the other.")}
            </p>
          </div>

          <p className="hpa-positioning-description">
            {t("One complete platform for hypertrophy, athletic performance, mobility and kettlebell training. Explore thousands of professionally demonstrated exercises and train with purpose—whether your goal is to build your physique, improve performance or do both.")}
          </p>

          <Link to="/libraries" className="btn btn-skew btn-lg btn-primary shadow-primary m-t20">
            <span>{t("Explore the Platform")}</span>
          </Link>
        </div>
      </div>

      {/* ── How it works ── */}
      <div className="row mt-5">
        <div className="col-12 text-center mb-5 wow fadeInUp">
          <h2 className="title">{t("How It Works")}</h2>
          <p className="mx-auto" style={{ maxWidth: 560, color: "#666" }}>
            From registration to streaming your first coaching session — here's exactly
            how Hybrid Performance Academy works.
          </p>
        </div>

        {steps.map((step, i) => (
          <div key={i} className="col-lg-3 col-sm-6 mb-4 wow fadeInUp" data-wow-delay={`${i * 0.15}s`}>
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "32px 24px",
                height: "100%",
                boxShadow: "0 2px 20px rgba(0,0,0,.07)",
                borderTop: "4px solid var(--primary, #c0392b)",
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 16,
                  right: 20,
                  fontSize: 36,
                  fontWeight: 900,
                  color: "rgba(192,57,43,.08)",
                  lineHeight: 1,
                }}
              >
                {step.number}
              </span>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "var(--primary, #c0392b)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 18,
                }}
              >
                <i className={`fa-solid ${step.icon}`} style={{ color: "#fff", fontSize: 20 }} />
              </div>
              <h5 style={{ fontWeight: 700, marginBottom: 10 }}>{t(step.title)}</h5>
              <p style={{ fontSize: 14, color: "#666", margin: 0, lineHeight: 1.7 }}>{t(step.desc)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Feature grid ── */}
      <div className="row mt-5">
        <div className="col-12 text-center mb-5 wow fadeInUp">
          <h5 className="sub-title text-primary">{t("WHAT YOU GET")}</h5>
          <h2 className="title">{t("Platform Features")}</h2>
        </div>

        {features.map((f, i) => (
          <div key={i} className="col-lg-4 col-sm-6 mb-4 wow fadeInUp" data-wow-delay={`${i * 0.1}s`}>
            <div className="icon-bx-wraper style-4 bg-white m-b10">
              <div className="icon-bx icon-bg-white m-b20 align-items-center">
                <div className="icon-cell text-primary rounded-circle m-r10">
                  <i className={`fa-solid ${f.icon}`} />
                </div>
                <h4 className="dz-title m-b10">{t(f.title)}</h4>
              </div>
              <div className="icon-content">
                <p className="m-b0" style={{ fontSize: 14, color: "#666" }}>{t(f.desc)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── CTA strip ── */}
      <section className="hpa-comparison wow fadeInUp" aria-labelledby="hpa-comparison-title">
        <div className="hpa-comparison__heading">
          <div><span className="hpa-comparison__kicker">{t("WHY HPA IS DIFFERENT")}</span>
            <h2 id="hpa-comparison-title">{t("DON'T TRAIN IN ONE DIMENSION.")}</h2></div>
          <p>{t("Training should develop more than how you look. Compare random workouts with a complete performance system.")}</p>
        </div>
        <div className="hpa-comparison__grid">
          <article className="hpa-comparison-card hpa-comparison-card--traditional">
            <header><span className="hpa-comparison-card__icon"><i className="fa-solid fa-minus" /></span>
              <div><small>{t("The conventional way")}</small><h3>{t("Traditional Training")}</h3></div></header>
            <ul>{comparisonRows.map(([traditional], index) => <li key={traditional}>
              <span className="hpa-comparison-card__marker">{String(index + 1).padStart(2, "0")}</span>
              <span>{t(traditional)}</span><i className="fa-solid fa-xmark" aria-hidden="true" />
            </li>)}</ul>
          </article>
          <article className="hpa-comparison-card hpa-comparison-card--hpa">
            <span className="hpa-comparison-card__badge">{t("THE COMPLETE SYSTEM")}</span>
            <header><span className="hpa-comparison-card__icon"><i className="fa-solid fa-bolt" /></span>
              <div><small>{t("Train without limits")}</small><h3>{t("Hybrid Performance Academy")}</h3></div></header>
            <ul>{comparisonRows.map(([, academy], index) => <li key={academy}>
              <span className="hpa-comparison-card__marker">{String(index + 1).padStart(2, "0")}</span>
              <span>{t(academy)}</span><i className="fa-solid fa-check" aria-hidden="true" />
            </li>)}</ul>
          </article>
        </div>
        <div className="hpa-comparison__cta">
          <div><span>{t("Built for complete performance")}</span>
            <h3>{t("Your body wasn't built for one thing. Your training shouldn't be either.")}</h3></div>
          <Link to="/packages" className="btn btn-primary btn-skew btn-lg"><span>{t("Join the Academy")}</span></Link>
        </div>
      </section>
    </>
  );
};

export default AboutServices;

export const AboutServicesDetails = () => null;
